export const currency = (n) => {
  const num = Number(n || 0);
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(num);
};

export const todayIso = () => {
  const d = new Date();
  const tz = d.getTimezoneOffset() * 60000;
  return new Date(d - tz).toISOString().slice(0, 10);
};

export const tomorrowIso = () => {
  const d = new Date(Date.now() + 86400000);
  const tz = d.getTimezoneOffset() * 60000;
  return new Date(d - tz).toISOString().slice(0, 10);
};

// TZ-safe date arithmetic. We deliberately avoid toISOString() anywhere
// the result is a date-only string — the UTC conversion shifts the date
// for users east of UTC (IST +5:30 rolls back a day, etc.). Instead we
// parse [YYYY, MM, DD] as a local-midnight Date, add/subtract days in
// local time, and format back using local getters. This keeps dates
// stable across every timezone the app may run in.
export const addDaysIso = (iso, days) => {
  if (!iso) return "";
  const [y, m, d] = iso.split("-").map(Number);
  const dt = new Date(y, (m || 1) - 1, d || 1);
  dt.setDate(dt.getDate() + (days || 0));
  const yy = dt.getFullYear();
  const mm = String(dt.getMonth() + 1).padStart(2, "0");
  const dd = String(dt.getDate()).padStart(2, "0");
  return `${yy}-${mm}-${dd}`;
};

export const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso + "T00:00:00").toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return iso;
  }
};

// Round 97.3 — Convert "HH:MM" (24h) into a friendly "h:MM AM/PM"
// display string. Used by vendor engagement PDF + card labels.
export const formatTime12h = (raw) => {
  if (!raw) return "";
  const m = /^(\d{1,2}):(\d{2})/.exec(String(raw).trim());
  if (!m) return String(raw);
  let h = parseInt(m[1], 10);
  const mm = m[2];
  const ampm = h >= 12 ? "PM" : "AM";
  h = h % 12;
  if (h === 0) h = 12;
  return `${h}:${mm} ${ampm}`;
};

// Round 126.5 — Short relative time like "just now", "3 min ago",
// "2 h ago", "5 d ago". Used by live presence badges. Accepts an ISO
// string or a Date; returns "" if unparseable.
export const relativeTimeShort = (iso) => {
  if (!iso) return "";
  const then = iso instanceof Date ? iso.getTime() : Date.parse(iso);
  if (!Number.isFinite(then)) return "";
  const secs = Math.max(0, (Date.now() - then) / 1000);
  if (secs < 45) return "just now";
  const mins = Math.round(secs / 60);
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs} h ago`;
  const days = Math.round(hrs / 24);
  if (days < 7) return `${days} d ago`;
  const weeks = Math.round(days / 7);
  if (weeks < 4) return `${weeks} w ago`;
  const months = Math.round(days / 30);
  return `${months} mo ago`;
};


// ============================================================
// Order categorization (new business logic)
// Status derives from (paid ? total received : balance due) and
// (event date passed ? completed : open)
// ============================================================
export const isFullyPaid = (o) =>
  Number(o?.advance_amount || 0) >= Number(o?.total_amount || 0) && Number(o?.total_amount || 0) > 0
    ? true
    : Number(o?.advance_amount || 0) > 0 &&
      Number(o?.advance_amount || 0) >= Number(o?.total_amount || 0);

export const orderCategory = (o) => {
  const today = todayIso();
  const paid =
    Number(o.total_amount || 0) > 0 &&
    Number(o.advance_amount || 0) >= Number(o.total_amount || 0);
  const eventPassed =
    (o.event_date && o.event_date < today) ||
    (!o.event_date && o.delivery_date && o.delivery_date < today);
  // Round 120.5 — Delivery-aware categories. Once the delivery has
  // been marked done (auto by trip_state.py or manually via the
  // "Mark Delivered" button) the top badge reflects that. Kept
  // separate from `eventPassed` so early deliveries still surface
  // as "Delivered, Paid / Due" rather than confusingly reading
  // "Open, Paid" for hours after the truck rolled away.
  const delivered = !!o.delivered_at;
  if (eventPassed && paid) return "closed";
  if (eventPassed && !paid) return "completed_pending";
  if (delivered && paid) return "delivered_received";
  if (delivered && !paid) return "delivered_due";
  if (!eventPassed && paid) return "open_received";
  return "open_due";
};

export const CATEGORY_META = {
  closed: {
    label: "Closed, Payment Received",
    short: "Closed, Paid",
    badge: "bg-emerald-100 text-emerald-800 hover:bg-emerald-100",
  },
  completed_pending: {
    label: "Completed, Payment Pending",
    short: "Completed, Pending",
    badge: "bg-amber-100 text-amber-800 hover:bg-amber-100",
  },
  // Round 120.5 — Delivered but the event hasn't happened yet.
  delivered_received: {
    label: "Delivered, Payment Received",
    short: "Delivered, Paid",
    badge: "bg-emerald-100 text-emerald-800 hover:bg-emerald-100",
  },
  delivered_due: {
    label: "Delivered, Payment Due",
    short: "Delivered, Due",
    badge: "bg-amber-100 text-amber-800 hover:bg-amber-100",
  },
  open_received: {
    label: "Open, Paid",
    short: "Open, Paid",
    badge: "bg-blue-100 text-blue-800 hover:bg-blue-100",
  },
  open_due: {
    label: "Open, Payment Due",
    short: "Open, Due",
    badge: "bg-red-100 text-red-800 hover:bg-red-100",
  },
};

// Delivery status for logistics scheduling
export const deliveryStatus = (iso) => {
  if (!iso) return { key: "pending", label: "Pending" };
  const t = todayIso();
  const tomorrow = addDaysIso(t, 1);
  if (iso === t) return { key: "due_today", label: "Due Today" };
  if (iso === tomorrow) return { key: "due_tomorrow", label: "Due Tomorrow" };
  if (iso < t) return { key: "completed", label: "Completed" };
  return { key: "pending", label: "Pending" };
};

// ============================================================
// WhatsApp share helpers
// ============================================================
// Basic link — phone + free-form body text (supports Object overload for backwards compat with orders)
export const whatsappLink = (phoneOrOrder, body = "") => {
  // Overload: if first arg is an order-shaped object, build the standard customer reminder
  if (phoneOrOrder && typeof phoneOrOrder === "object") {
    return customerReminderLink(phoneOrOrder);
  }
  const phone = String(phoneOrOrder || "").replace(/\D/g, "");
  const text = encodeURIComponent(body || "");
  return phone ? `https://wa.me/91${phone}?text=${text}` : `https://wa.me/?text=${text}`;
};

export const ADAMS_REVIEW_URL = "https://maps.app.goo.gl/5XpZpsZNaUBPfzZD6?g_st=ic";

export const customerReminderLink = (order) => {
  const phone = (order.customer_phone || "").replace(/\D/g, "");
  const cat = orderCategory(order);
  const balance =
    Number(order.total_amount || 0) - Number(order.advance_amount || 0);
  const itemsLine =
    (order.items || [])
      .filter((it) => isInventoryItem(it.name))
      .map((it) => `• ${it.name} × ${it.quantity}`)
      .slice(0, 8)
      .join("\n") || "—";

  let lines;
  if (cat === "closed") {
    // Event done + paid in full → feedback / review request.
    lines = [
      `Hello ${order.customer_name || "Customer"},`,
      "",
      "Thank you for choosing *Adams – Event Furniture Rentals* for your recent event.",
      "We hope everything went beautifully and our team made things easier for you.",
      "",
      "If you have a moment, we would be truly grateful if you could share your experience as a Google review — it helps our small team a lot.",
      "",
      `⭐ Leave a review: ${ADAMS_REVIEW_URL}`,
      "",
      "Looking forward to serving you again soon!",
      "— Team Adams",
    ];
  } else if (cat === "completed_pending") {
    // Event done + unpaid → payment reminder.
    lines = [
      `Hello ${order.customer_name || "Customer"},`,
      "",
      "This is a gentle reminder from *Adams – Event Furniture Rentals* regarding your recent event.",
      order.event_date ? `🎉 Event date: ${formatDate(order.event_date)}` : "",
      "",
      `💰 *Balance due: ₹${balance.toLocaleString("en-IN")}*`,
      "",
      "Kindly arrange the pending payment at your earliest convenience. You may pay in cash, by UPI or bank transfer — happy to share details on request.",
      "",
      "Thank you for choosing Adams.",
    ].filter(Boolean);
  } else {
    // Order is OPEN — delivery reminder (with balance line if any).
    lines = [
      `Hello ${order.customer_name || "Customer"},`,
      "",
      "This is a reminder from *Adams – Event Furniture Rentals*.",
      `📅 Delivery: ${formatDate(order.delivery_date)}`,
      order.return_date ? `↩️ Return: ${formatDate(order.return_date)}` : "",
      order.event_date ? `🎉 Event: ${formatDate(order.event_date)}` : "",
      "",
      "Items:",
      itemsLine,
      "",
      balance > 0
        ? `💰 Balance due: ₹${balance.toLocaleString("en-IN")}`
        : "✅ Payment received in full.",
      "",
      "For queries please call us.",
      "Thank you!",
    ].filter(Boolean);
  }

  const text = encodeURIComponent(lines.join("\n"));
  return phone ? `https://wa.me/91${phone}?text=${text}` : `https://wa.me/?text=${text}`;
};

// Round 64 — Universal "share client dashboard" WhatsApp link.
// Builds a premium, warm message that links the customer to their
// interactive event dashboard (status, journey, feedback). Used by the
// universal Share button shown next to every customer name across the app.
export const clientDashboardLink = (order) => {
  if (!order?.id) return "";
  const origin =
    typeof window !== "undefined" && window.location?.origin
      ? window.location.origin
      : "";
  return `${origin}/client/${encodeURIComponent(order.id)}`;
};

export const shareClientDashboardWaLink = (order) => {
  const phone = (order?.customer_phone || "").replace(/\D/g, "");
  const url = clientDashboardLink(order);
  const lines = [
    `Hello ${order?.customer_name || "there"} 👋`,
    "",
    "Greetings from *Adams — Event Furniture Rentals*.",
    `Here is your private event dashboard for Order #${order?.order_number || ""}:`,
    "",
    url,
    "",
    "On this page you can:",
    "• See your full order, items and balance",
    "• Track delivery, event and return status live",
    "• Share with family / planner / venue manager",
    "• Leave us a quick feedback after the event 🌟",
    "",
    "Feel free to bookmark this link — it stays active for your event.",
    "Looking forward to making it a beautiful experience for you.",
    "",
    "— Team Adams",
  ];
  const text = encodeURIComponent(lines.join("\n"));
  return phone ? `https://wa.me/91${phone}?text=${text}` : `https://wa.me/?text=${text}`;
};

// Identify inventory items only (exclude TDS/GST/transport rows AI may extract)
export const isInventoryItem = (name) => {
  const n = (name || "").toLowerCase();
  if (!n.trim()) return false;
  const skipPatterns = [
    /\btds\b/,
    /\btsd\b/,
    /\btransport\b/,
    /\bloading\b/,
    /\bunloading\b/,
    /\bgst\b/,
    /\bigst\b/,
    /\bcgst\b/,
    /\bsgst\b/,
    /\btax\b/,
    /\bservice\s*charge\b/,
    /\bdamage(s|d)?\b/,
    /\bmissing\b/,
    /\blost\b/,
    /\blate\s*(fee|charge)\b/,
    /\bpenalty\b/,
    /\bdiscount\b/,
    /\bround\s*off\b/,
    /\bsub[-\s]*total\b/,
    /\bgrand\s*total\b/,
  ];
  return !skipPatterns.some((rx) => rx.test(n));
};
